import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import type { EpocusConsentDocumentItem } from "../../../../../global-providers/rxdb-provider/collections/epocus-consent-document";
import type {
  EpocusConsentAudienceValue,
  EpocusConsentResponseValue,
} from "../../../../../global-providers/rxdb-provider/collections/epocus-patient-consent";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";

type Answers = Partial<Record<string, EpocusConsentResponseValue>>;

const TERMS_CONFIRMATION_LABEL =
  "Do you confirm you are happy for you/your child's information to be processed in this way?";

const QUESTIONS_INSTRUCTION =
  "Last step. You need to ask the patient the following questions about how their data can be used.";

const AUDIENCE_QUESTION = "Who are you asking for consent?";

const AUDIENCE_INSTRUCTION =
  "The questions and the terms are worded differently depending on the answer, so choose before you begin.";

const GUARDIAN_QUESTIONS_INSTRUCTION =
  "Last step. You need to ask the patient's parent or guardian about how their child's data can be used.";

const PRIVACY_POLICY_URL = "https://privacy.elephant.healthcare/privacy-policy";

const CLOSING_NOTE =
  "You can change your answers at any time by contacting the organisation through their website, or we can message them for you.";

export const PatientConsentDialog: FC<{
  open: boolean;
  audience: EpocusConsentAudienceValue | null;
  onAudienceChange: (audience: EpocusConsentAudienceValue) => void;
  termsItem: EpocusConsentDocumentItem | null;
  questionItems: EpocusConsentDocumentItem[];
  isSubmitting: boolean;
  onSubmit: (answers: Record<string, EpocusConsentResponseValue>) => void;
  onCancel: () => void;
}> = ({
  open,
  audience,
  onAudienceChange,
  termsItem,
  questionItems,
  isSubmitting,
  onSubmit,
  onCancel,
}) => {
  const [answers, setAnswers] = useState<Answers>({});
  const [termsConfirmed, setTermsConfirmed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (audience === null) return;
    contentRef.current?.focus();
  }, [audience]);

  const allAnswered = questionItems.every(
    (item) => answers[item.id] !== undefined,
  );
  const termsSatisfied = termsItem === null || termsConfirmed;
  const canSubmit = allAnswered && termsSatisfied;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(answers as Record<string, EpocusConsentResponseValue>);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={isSubmitting ? undefined : onCancel}
      PaperProps={{
        style: { backgroundColor: "transparent", boxShadow: "none" },
      }}
    >
      <div
        ref={contentRef}
        tabIndex={-1}
        style={{
          borderRadius: "16px 16px 0 0",
          padding: "20px 24px 32px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: EPOCUS_WHITE,
          maxWidth: 680,
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Typography
          style={{
            fontSize: rem(20),
            fontWeight: 700,
            color: EPOCUS_TEXT_PRIMARY,
            marginBottom: 16,
          }}
        >
          Patient Consent
        </Typography>

        <Box style={{ overflowY: "auto", minHeight: 0 }}>
          {audience === null && (
            <Box
              style={{
                backgroundColor: EPOCUS_BG,
                border: `1px solid ${EPOCUS_BORDER}`,
                borderRadius: 10,
                padding: 16,
                marginBottom: 16,
              }}
            >
              <Typography
                style={{
                  fontSize: rem(16),
                  fontWeight: 700,
                  color: EPOCUS_TEXT_PRIMARY,
                  marginBottom: 4,
                }}
              >
                {AUDIENCE_QUESTION}
              </Typography>
              <Typography
                style={{
                  fontSize: rem(13),
                  color: EPOCUS_TEXT_SECONDARY,
                  marginBottom: 12,
                }}
              >
                {AUDIENCE_INSTRUCTION}
              </Typography>
              <RadioGroup
                aria-label={AUDIENCE_QUESTION}
                name="consent-audience"
                onChange={(e) =>
                  onAudienceChange(e.target.value as EpocusConsentAudienceValue)
                }
              >
                <FormControlLabel
                  value="ADULT"
                  control={<Radio color="primary" />}
                  label="The patient"
                />
                <FormControlLabel
                  value="GUARDIAN"
                  control={<Radio color="primary" />}
                  label="A parent or guardian, for a child"
                />
              </RadioGroup>
            </Box>
          )}

          {termsItem && audience !== null && (
            <Box
              style={{
                backgroundColor: EPOCUS_BG,
                border: `1px solid ${EPOCUS_BORDER}`,
                borderRadius: 10,
                padding: 16,
                marginBottom: 16,
              }}
            >
              <Typography
                style={{
                  fontSize: rem(11),
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: EPOCUS_TEXT_SECONDARY,
                  marginBottom: 8,
                }}
              >
                Terms of Registration
              </Typography>
              <Typography
                style={{
                  fontSize: rem(14),
                  color: EPOCUS_TEXT_PRIMARY,
                  whiteSpace: "pre-wrap",
                }}
              >
                {termsItem.bodyText}
              </Typography>
              <FormControlLabel
                style={{ marginTop: 8, alignItems: "flex-start" }}
                control={
                  <Checkbox
                    color="primary"
                    checked={termsConfirmed}
                    onChange={(e) => setTermsConfirmed(e.target.checked)}
                    inputProps={{ "aria-label": TERMS_CONFIRMATION_LABEL }}
                  />
                }
                label={
                  <Typography
                    style={{ fontSize: rem(14), color: EPOCUS_TEXT_PRIMARY }}
                  >
                    {TERMS_CONFIRMATION_LABEL}
                  </Typography>
                }
              />
            </Box>
          )}

          {audience !== null && (
            <Box
              style={{
                backgroundColor: EPOCUS_BG,
                border: `1px solid ${EPOCUS_BORDER}`,
                borderRadius: 10,
                padding: 16,
                marginBottom: 20,
              }}
            >
              <Typography
                style={{
                  fontSize: rem(11),
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: EPOCUS_TEXT_SECONDARY,
                  marginBottom: 8,
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                style={{ fontSize: rem(14), color: EPOCUS_TEXT_PRIMARY }}
              >
                Elephant processes this information in accordance with its{" "}
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: EPOCUS_PRIMARY }}
                >
                  Privacy Policy
                </a>
                .
              </Typography>
            </Box>
          )}

          {audience !== null && questionItems.length > 0 && (
            <Typography
              style={{
                fontSize: rem(13),
                fontStyle: "italic",
                color: EPOCUS_TEXT_SECONDARY,
                marginBottom: 12,
              }}
            >
              {audience === "GUARDIAN"
                ? GUARDIAN_QUESTIONS_INSTRUCTION
                : QUESTIONS_INSTRUCTION}
            </Typography>
          )}

          {audience !== null && (
            <Box style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {questionItems.map((item) => (
                <Box key={item.id}>
                  <Typography
                    style={{
                      fontSize: rem(14),
                      fontWeight: 600,
                      color: EPOCUS_TEXT_PRIMARY,
                      marginBottom: 4,
                    }}
                  >
                    {item.bodyText}
                  </Typography>
                  <RadioGroup
                    row
                    aria-label={item.bodyText}
                    name={`consent-question-${item.id}`}
                    value={answers[item.id] ?? ""}
                    onChange={(e) =>
                      setAnswers((prev) => ({
                        ...prev,
                        [item.id]: e.target.value as EpocusConsentResponseValue,
                      }))
                    }
                  >
                    <FormControlLabel
                      value="YES"
                      control={<Radio color="primary" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="NO"
                      control={<Radio color="primary" />}
                      label="No"
                    />
                    {audience === "GUARDIAN" && (
                      <FormControlLabel
                        value="NO_PARENT_PRESENT"
                        control={<Radio color="primary" />}
                        label="No parent present"
                      />
                    )}
                  </RadioGroup>
                </Box>
              ))}
            </Box>
          )}

          {audience !== null && questionItems.length > 0 && (
            <Typography
              style={{
                fontSize: rem(13),
                color: EPOCUS_TEXT_SECONDARY,
                marginTop: 16,
              }}
            >
              {CLOSING_NOTE}
            </Typography>
          )}
        </Box>

        {audience !== null && (
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            disableElevation
            style={{
              backgroundColor:
                !canSubmit || isSubmitting ? undefined : EPOCUS_PRIMARY,
              color: !canSubmit || isSubmitting ? undefined : EPOCUS_WHITE,
              borderRadius: 10,
              padding: "14px 0",
              fontSize: rem(16),
              fontWeight: 700,
              textTransform: "none",
              marginTop: 20,
              minHeight: 52,
            }}
          >
            {isSubmitting ? "Saving…" : "Save Consent"}
          </Button>
        )}
        <Button
          fullWidth
          onClick={onCancel}
          disabled={isSubmitting}
          style={{
            borderRadius: 10,
            padding: "12px 0",
            fontSize: rem(15),
            fontWeight: 600,
            textTransform: "none",
            marginTop: 8,
            color: EPOCUS_TEXT_SECONDARY,
          }}
        >
          Cancel
        </Button>
      </div>
    </Drawer>
  );
};
